import React, { ChangeEvent } from 'react';
import styled from 'styled-components';

import FieldSlideOutSection from './FieldSlideOutSection';
import Typography from '../../../../kingpin/atoms/Typography';
import Inputs from '../../../../components/Inputs';
import FieldCellTypeDropdown from '../FieldCellTypeDropdown';
import CurrencyTypeDropdown from './CurrencyTypeDropdown';
import { GEO_POINT_CELL } from '../../../../components/Grid/constants';
import GeoPointFormatInput from './GeoPointFormatInput';
import FormattingInput from '../../../../components/FormattingInput';
import IsQueryDateUseDisabledToggle from './IsQueryDateUseDisabledToggle';
import BonusScoreColourSettings from './BonusScoreColourSettings';

// Cell types that expose the grouped FormattingInput (prefix/suffix/precision/
// currency/"increase is desirable"). For these the standalone "Positive Delta is
// good" toggle is redundant — it edits the same formatting.positiveDeltaIsGood —
// so it is hidden and only shown inside the Formatting box.
const FORMATTING_INPUT_CELL_TYPES: FleetOps.CellType[] = [
  'Bonus Reward Field',
  'Bonus Expectation Field',
  'Colour Coded Number',
];

const FormattingCard = styled.div`
  background-color: var(--color-bg-muted);
  padding: 12px;
  border-radius: 8px;
`;

export interface FieldOptionsType {
  fieldDef: FleetOps.Field;
  fieldView: FleetOps.BaseViewField;
  baseView: FleetOps.BaseView;
  field: string;
  showInGrid: boolean;
  onShowInGridChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  displayName?: string;
  onDisplayNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  description?: string;
  onDescriptionChanged: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  totalOptions: DropdownOption[];
  totalsAggFunc?: AggFunc;
  cellType?: FleetOps.CellType;
  positiveDeltaIsGood: boolean;
  onPositiveDeltaIsGoodChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  dateFormatOptions: DropdownOption[];
  dateFormat?: FleetOps.DateFormat;
  precision?: number;
  onPrecisionChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  onClose: () => void;
  isCommasDisabled: boolean;
  onIsCommasDisabledChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  formatting: MetricFormatting;
  setFormatting: React.Dispatch<React.SetStateAction<MetricFormatting>>;
  updateView: (newView: FleetOps.BaseViewField) => void;
}

const FieldOptions = ({
  fieldDef,
  fieldView,
  baseView,
  showInGrid,
  onShowInGridChanged,
  displayName,
  onDisplayNameChanged,
  description,
  onDescriptionChanged,
  totalOptions,
  totalsAggFunc,
  cellType,
  positiveDeltaIsGood,
  onPositiveDeltaIsGoodChanged,
  dateFormatOptions,
  dateFormat,
  precision,
  onPrecisionChanged,
  isCommasDisabled,
  onIsCommasDisabledChanged,
  formatting,
  setFormatting,
  updateView,
}: FieldOptionsType) => (
  <>
    <FieldSlideOutSection>
      <div style={{ marginBottom: 4 }}>
        <Typography.Body type="Label">Enabled</Typography.Body>
      </div>
      <Inputs.Toggle
        value={showInGrid}
        onChange={(newValue) => {
          // @ts-ignore
          onShowInGridChanged({ target: { checked: newValue } });
        }}
        testId={'slideout-visible'}
      />
    </FieldSlideOutSection>
    <FieldSlideOutSection>
      <div style={{ marginBottom: 4 }}>
        <Typography.Body type="Label">Display Name</Typography.Body>
      </div>
      <Inputs.TextInput
        value={displayName || ''}
        onChange={onDisplayNameChanged}
        data-testid={'slideout-name'}
      />
    </FieldSlideOutSection>
    <FieldSlideOutSection>
      <div style={{ marginBottom: 4 }}>
        <Typography.Body type="Label">Description</Typography.Body>
      </div>
      <Inputs.TextArea
        value={description}
        placeholder={'Add a description...'}
        onChange={onDescriptionChanged}
        rows={description ? description.split('\n').length + 1 : 2}
        data-testid={'slideout-description'}
      />
    </FieldSlideOutSection>
    <FieldSlideOutSection>
      <div style={{ marginBottom: 4 }}>
        <Typography.Body type="Label">Totals Row formula</Typography.Body>
      </div>
      <Inputs.Dropdown
        options={totalOptions}
        selectedLabel={totalsAggFunc ? totalsAggFunc : 'None'}
        testId={'slideout-totals'}
      />
    </FieldSlideOutSection>
    <FieldSlideOutSection>
      <div style={{ marginBottom: 4 }}>
        <Typography.Body type="Label">Cell Type</Typography.Body>
      </div>
      <FieldCellTypeDropdown
        field={fieldDef}
        fieldView={fieldView}
        baseView={baseView}
        testId={'slideout-cellType'}
      />
    </FieldSlideOutSection>
    {cellType === 'Bonus Expectation Field' && (
      <FieldSlideOutSection>
        <div style={{ marginBottom: 4 }}>
          <Typography.Body type="Label">Below target is good</Typography.Body>
        </div>
        <Inputs.Toggle
          value={!!fieldView.belowTargetIsGood}
          onChange={(newValue) =>
            updateView({ ...fieldView, belowTargetIsGood: newValue })
          }
        />
      </FieldSlideOutSection>
    )}
    {cellType && cellType === 'Currency' && (
      <FieldSlideOutSection>
        <div style={{ marginBottom: 4 }}>
          <Typography.Body type="Label">Currency Type</Typography.Body>
        </div>
        <CurrencyTypeDropdown
          field={fieldDef}
          fieldView={fieldView}
          baseView={baseView}
          testId={'slideout-currencyType'}
        />
      </FieldSlideOutSection>
    )}
    {fieldDef.type !== 'geo_point' &&
      !(cellType && FORMATTING_INPUT_CELL_TYPES.includes(cellType)) && (
        <FieldSlideOutSection>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">
              Positive Delta is good
            </Typography.Body>
          </div>
          <Inputs.Toggle
            value={positiveDeltaIsGood}
            onChange={(newValue) => {
              onPositiveDeltaIsGoodChanged({
                // @ts-ignore
                target: {
                  checked: newValue,
                },
              });
            }}
          />
        </FieldSlideOutSection>
      )}
    {cellType &&
      (cellType === 'Currency' ||
        cellType === 'Number' ||
        cellType === 'Percentage') && (
        <>
          {(fieldDef.type === 'float' || fieldDef.type === 'long') && (
            <FieldSlideOutSection>
              <div style={{ marginBottom: 4 }}>
                <Typography.Body type="Label">Precision</Typography.Body>
              </div>
              <Inputs.TextInput
                type="number"
                value={
                  precision !== undefined && !isNaN(precision)
                    ? precision.toString(10)
                    : ''
                }
                onChange={onPrecisionChanged}
              />
            </FieldSlideOutSection>
          )}
          <FieldSlideOutSection>
            <div style={{ marginBottom: 4 }}>
              <Typography.Body type="Label">
                Use comma (,) separator
              </Typography.Body>
            </div>
            <Inputs.Toggle
              value={!isCommasDisabled}
              onChange={(newValue) => {
                onIsCommasDisabledChanged({
                  // @ts-ignore
                  target: {
                    checked: newValue,
                  },
                });
              }}
            />
          </FieldSlideOutSection>
        </>
      )}
    {cellType && cellType === GEO_POINT_CELL && (
      <FieldSlideOutSection>
        <Typography.Body type="Label">Lat Long Order</Typography.Body>
        <GeoPointFormatInput fieldView={fieldView} updateView={updateView} />
      </FieldSlideOutSection>
    )}
    {cellType && cellType === 'Date' && (
      <FieldSlideOutSection>
        <Typography.Body type="Label">Date Format</Typography.Body>
        <Inputs.Dropdown
          options={dateFormatOptions}
          selectedLabel={dateFormat ? dateFormat : 'None'}
        />
      </FieldSlideOutSection>
    )}
    {cellType && FORMATTING_INPUT_CELL_TYPES.includes(cellType) && (
      <FieldSlideOutSection>
        <FormattingCard>
          <div style={{ marginBottom: 8 }}>
            <Typography.Body type="Label">Formatting</Typography.Body>
          </div>
          <FormattingInput
            formatting={formatting}
            setFormatting={setFormatting}
          />
        </FormattingCard>
      </FieldSlideOutSection>
    )}
    {(cellType === 'Bonus Score Field' ||
      cellType === 'Colour Coded Number') && (
      <BonusScoreColourSettings fieldView={fieldView} updateView={updateView} />
    )}
    <IsQueryDateUseDisabledToggle
      fieldDef={fieldDef}
      fieldView={fieldView}
      updateView={updateView}
    />
  </>
);

export default FieldOptions;
